/* @jsxImportSource react */
'use client';

import { cn } from '../../utils/cn';

/** Loader size variant definitions mapping sizes to their Tailwind classes. */
export const ATOMS_LOADER_VARIANTS = {
  size: {
    sm: {
      classes: 'h-4 w-4',
      description: 'Small loader for inline use',
    },
    base: {
      classes: 'h-6 w-6',
      description: 'Default loader size',
    },
    lg: {
      classes: 'h-8 w-8',
      description: 'Large loader for prominent loading states',
    },
    xl: {
      classes: 'h-10 w-10',
      description: 'Extra-large loader (40px)',
    },
    '2xl': {
      classes: 'h-12 w-12',
      description: '2x-large loader (48px)',
    },
    '3xl': {
      classes: 'h-16 w-16',
      description: '3x-large loader (64px) for full-page loading states',
    },
  },
} as const;

export const ATOMS_LOADER_DEFAULT_VARIANTS = {
  size: 'base',
} as const;

// Derived types from ATOMS_LOADER_VARIANTS
export type AtomsLoaderSize = keyof typeof ATOMS_LOADER_VARIANTS.size;

export interface AtomsLoaderVariantsProps {
  /**
   * Size of the loader.
   * - `"sm"` — 16px, small loader for inline use
   * - `"base"` — 24px, default loader size
   * - `"lg"` — 32px, large loader for prominent loading states
   * - `"xl"` — 40px
   * - `"2xl"` — 48px
   * - `"3xl"` — 64px, for full-page loading states
   *
   * For arbitrary sizes, pass a sizing utility via `className`
   * (e.g. `className="h-10 w-10"`).
   * @default "base"
   */
  size?: AtomsLoaderSize;
}

export function loaderVariants({
  size = ATOMS_LOADER_DEFAULT_VARIANTS.size,
}: AtomsLoaderVariantsProps = {}): string {
  return ATOMS_LOADER_VARIANTS.size[size].classes;
}

/**
 * Loader component props.
 *
 * @example
 * ```tsx
 * <Loader />
 * <Loader size="sm" />
 * <Loader className="h-10 w-10" />
 * ```
 */
export interface LoaderProps {
  /** Additional CSS classes merged via `cn()`. */
  className?: string;
  /**
   * Size of the spinner.
   * - `"sm"` — 16px, for inline use
   * - `"base"` — 24px, default size
   * - `"lg"` — 32px, for prominent loading states
   * - `"xl"` — 40px
   * - `"2xl"` — 48px
   * - `"3xl"` — 64px, for full-page loading states
   *
   * For arbitrary sizes, pass a sizing utility via `className`
   * (e.g. `className="h-10 w-10"`).
   * @default "base"
   */
  size?: AtomsLoaderSize;
  /**
   * Accessible label for the loader, announced by screen readers.
   * Pass a translated string for internationalization.
   * @default "Loading"
   */
  'aria-label'?: string;
}

/**
 * Animated circular spinner for indicating loading states.
 *
 * @example
 * ```tsx
 * <Loader />
 * ```
 */
export const Loader = ({
  className,
  size = ATOMS_LOADER_DEFAULT_VARIANTS.size,
  'aria-label': ariaLabel = 'Loading',
}: LoaderProps) => {
  return (
    <svg
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      stroke="currentColor"
      // The size className must come before the consumer's className so the
      // consumer can override via `h-* w-*` utilities (tailwind-merge resolves).
      className={cn(loaderVariants({ size }), className)}
      // eslint-disable-next-line jsx-a11y/prefer-tag-over-role -- span as status is standard for loaders
      role="status"
      aria-label={ariaLabel}
    >
      <circle cx="12" cy="12" r="9.5" fill="none" strokeWidth="2" strokeLinecap="round">
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 12 12"
          to="360 12 12"
          dur="2s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="stroke-dasharray"
          values="0 150;42 150;42 150"
          keyTimes="0;0.5;1"
          dur="1.5s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="stroke-dashoffset"
          values="0;-16;-59"
          keyTimes="0;0.5;1"
          dur="1.5s"
          repeatCount="indefinite"
        />
      </circle>
      <circle
        cx="12"
        cy="12"
        r="9.5"
        fill="none"
        opacity={0.1}
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
};
